import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../../auth";
import { BUILDER_BASE } from "./formBuilder/constants";
import Card from "./formBuilder/Card";
import DraftOptionsInput from "./formBuilder/DraftOptionsInput";
import FieldHint from "./formBuilder/FieldHint";
import { hintsPt } from "./formBuilder/hintsPt";
import { parseOptionEntryText } from "../../utils/formBuilderOptions";
import { FaTrashAlt } from "react-icons/fa";

function getSuperQuestionId(q) {
  // Compatibilidade: a documentação nova usa `section`; versões antigas usavam `super_question`.
  const v = q?.section ?? q?.super_question;
  return v && typeof v === "object" ? String(v.id) : v == null ? "" : String(v);
}

function hasQuestionChanged(current, original) {
  return (
    (current?.label || "") !== (original?.label || "") ||
    (current?.type || "text") !== (original?.type || "text") ||
    !!current?.required !== !!original?.required ||
    getSuperQuestionId(current) !== getSuperQuestionId(original)
  );
}

export default function FormBuilderEditPage() {
  const navigate = useNavigate();
  const { formId } = useParams();
  const formIdStr = formId != null && formId !== "" ? String(formId) : null;
  const sectionInputRef = useRef(null);
  const previewBuilderRef = useRef(null);
  const [allForms, setAllForms] = useState([]);
  const [loadingForm, setLoadingForm] = useState(true);
  const [formSwitcherOpen, setFormSwitcherOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState("");
  const [creatingQuestion, setCreatingQuestion] = useState(false);
  const [superQuestions, setSuperQuestions] = useState([]);
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionLabel, setNewSectionLabel] = useState("");
  const [newSectionParentId, setNewSectionParentId] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [draftQuestion, setDraftQuestion] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [draftAnchorId, setDraftAnchorId] = useState(null);
  const [builderToast, setBuilderToast] = useState(null);
  const [addingOption, setAddingOption] = useState(false);
  const [editingForm, setEditingForm] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [formDraft, setFormDraft] = useState(null); // { title, description, code, target_group, active }
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionLabel, setEditingSectionLabel] = useState("");
  const [savingSection, setSavingSection] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [editingOptionLabel, setEditingOptionLabel] = useState("");
  const [savingOption, setSavingOption] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [conditionsError, setConditionsError] = useState("");
  const [creatingCondition, setCreatingCondition] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState(null);
  const [conditionDraft, setConditionDraft] = useState({
    source_question: "",
    operator: "equals",
    option: "",
    value: "",
  });
  const [savingCondition, setSavingCondition] = useState(false);
  const [draftOptionInput, setDraftOptionInput] = useState("");
  const [draftOptionItems, setDraftOptionItems] = useState([]);
  const [draftOptionJustAdded, setDraftOptionJustAdded] = useState(null);

  function addDraftOptionLabels(labels) {
    const parts = (Array.isArray(labels) ? labels : parseOptionEntryText(labels)).filter(Boolean);
    if (parts.length === 0) return;
    setDraftOptionItems((prev) => {
      const seen = new Set(prev.map((x) => x.toLowerCase()));
      const next = [...prev];
      let lastAdded = null;
      for (const p of parts) {
        const key = p.toLowerCase();
        if (!seen.has(key)) {
          next.push(p);
          seen.add(key);
          lastAdded = p;
        }
      }
      if (lastAdded) setDraftOptionJustAdded(lastAdded);
      return next;
    });
  }

  function clearDraftOptions() {
    setDraftOptionInput("");
    setDraftOptionItems([]);
    setDraftOptionJustAdded(null);
  }

  const handleBack = () => {
    navigate("/formularios");
  };

  async function loadAllForms() {
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/`);
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      setAllForms(Array.isArray(data) ? data : []);
    } catch (_) {
      setAllForms([]);
    }
  }

  useEffect(() => {
    loadAllForms();
  }, []);

  useEffect(() => {
    setSelectedQuestion(null);
    setDraftQuestion(null);
    setDraftAnchorId(null);
  }, [formIdStr]);

  useEffect(() => {
    if (!formIdStr) {
      navigate("/formularios", { replace: true });
      return;
    }
    let cancelled = false;
    setLoadingForm(true);
    setSelectedForm(null);
    (async () => {
      try {
        const resp = await authFetch(`${BUILDER_BASE}/forms/${formIdStr}/`);
        if (!resp.ok) throw new Error("notfound");
        const data = await resp.json();
        if (cancelled) return;
        setSelectedForm(data);
        await Promise.all([loadQuestions(data.id), loadSuperQuestions(data.id), loadConditions(data.id)]);
      } catch {
        if (!cancelled) navigate("/formularios", { replace: true });
      } finally {
        if (!cancelled) setLoadingForm(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formIdStr, navigate]);

  useEffect(() => {
    if (!builderToast) return;
    const id = setTimeout(() => setBuilderToast(null), 4200);
    return () => clearTimeout(id);
  }, [builderToast]);

  /** Mantém a pergunta em edição alinhada com a API (ex.: novas opções após POST /options/). */
  useEffect(() => {
    setSelectedQuestion((prev) => {
      if (!prev || prev.isDraft) return prev;
      const fresh = questions.find((q) => String(q.id) === String(prev.id));
      if (!fresh) return prev;
      if (hasQuestionChanged(prev, fresh)) {
        return {
          ...prev,
          options: Array.isArray(fresh.options) ? fresh.options : [],
        };
      }
      return fresh;
    });
  }, [questions]);

  function showBuilderToast(type, message) {
    setBuilderToast({ type, message });
  }

  async function loadQuestions(formId) {
    if (!formId) return;
    setLoadingQuestions(true);
    setQuestionsError("");
    const url = `${BUILDER_BASE}/forms/${formId}/questions/`;
    try {
      const resp = await authFetch(url);
      let detail = null;
      let isArray = false;
      try {
        const data = await resp.clone().json();
        isArray = Array.isArray(data);
        detail = data && typeof data === "object" ? data.detail : null;
        if (resp.ok) setQuestions(isArray ? data : []);
      } catch (_) {
        // ignore json parse errors
      }
      if (!resp.ok) {
        const isNoQuestions404 =
          resp.status === 404 &&
          typeof detail === "string" &&
          detail.toLowerCase().includes("no question matches");
        if (isNoQuestions404) {
          setQuestions([]);
          setQuestionsError("");
          return;
        }
        throw new Error(detail || `Erro ${resp.status}`);
      }
    } catch (e) {
      setQuestionsError(e?.message || "Erro ao carregar perguntas do formulário");
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  }

  async function loadSuperQuestions(formId) {
    if (!formId) return;
    try {
      const resp = await authFetch(`${BUILDER_BASE}/sections/`);
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      const all = Array.isArray(data) ? data : [];
      const filtered = all.filter(
        (s) =>
          String(s.form) === String(formId) ||
          (s.form && s.form.id && String(s.form.id) === String(formId))
      );
      setSuperQuestions(filtered);
    } catch (e) {
      // Não bloqueia o resto do editor se falhar
      setSuperQuestions([]);
    }
  }

  async function loadConditions(formId) {
    if (!formId) return;
    setLoadingConditions(true);
    setConditionsError("");
    try {
      const resp = await authFetch(`${BUILDER_BASE}/forms/${formId}/questions-conditions/`);
      if (resp.ok) {
        const data = await resp.json();
        setConditions(Array.isArray(data) ? data : []);
        return;
      }
      // Algumas listas devolvem 404 quando vazias
      if (resp.status === 404) {
        setConditions([]);
        return;
      }
      const txt = await resp.text();
      throw new Error(txt || `Erro ${resp.status} ao carregar condições`);
    } catch (e) {
      setConditions([]);
      setConditionsError(e?.message || "Erro ao carregar condições");
    } finally {
      setLoadingConditions(false);
    }
  }

  const [newQuestion, setNewQuestion] = useState({
    label: "",
    type: "text",
    required: true,
  });

  const previewQuestionsBySection = React.useMemo(() => {
    const allQuestions = [...questions];
    const map = new Map();
    const byId = new Map(superQuestions.map((s) => [String(s.id), s]));
    const noSectionKey = "__none__";
    allQuestions.forEach((q) => {
      const secId = getSuperQuestionId(q);
      const key = secId ? String(secId) : noSectionKey;
      const arr = map.get(key) || [];
      arr.push(q);
      map.set(key, arr);
    });
    const sections = superQuestions.map((s) => ({
      key: String(s.id),
      label: s.label,
      questions: map.get(String(s.id)) || [],
    }));
    const noSection = {
      key: noSectionKey,
      label: "Sem secção",
      questions: map.get(noSectionKey) || [],
    };
    if (draftQuestion) {
      const secId = getSuperQuestionId(draftQuestion) || noSectionKey;
      const target = secId === noSectionKey ? noSection.questions : (sections.find((s) => s.key === secId)?.questions || []);
      const idx = draftAnchorId ? target.findIndex((q) => String(q.id) === String(draftAnchorId)) : -1;
      if (idx >= 0) target.splice(idx + 1, 0, draftQuestion);
      else target.push(draftQuestion);
    }
    return { sections, noSection, byId };
  }, [questions, superQuestions, draftQuestion, draftAnchorId]);

  const renderPreviewField = (q) => {
    const common = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white";
    if (q.type === "textarea") return <textarea className={common} rows={3} placeholder="Resposta…" disabled />;
    if (q.type === "number") return <input className={common} type="number" placeholder="0" disabled />;
    if (q.type === "radio") {
      return (
        <div className="flex flex-col gap-2 max-h-48 overflow-auto pr-1">
          {(q.options || []).map((o) => (
            <label key={o.id || o.label} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" disabled />
              <span className="break-words">{o.label}</span>
            </label>
          ))}
          {Array.isArray(q.options) && q.options.length === 0 && (
            <div className="text-xs text-gray-400">Sem opções.</div>
          )}
        </div>
      );
    }
    if (q.type === "checkbox") {
      return (
        <div className="flex flex-col gap-2 max-h-48 overflow-auto pr-1">
          {(q.options || []).map((o) => (
            <label key={o.id || o.label} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" disabled />
              <span className="break-words">{o.label}</span>
            </label>
          ))}
          {Array.isArray(q.options) && q.options.length === 0 && (
            <div className="text-xs text-gray-400">Sem opções.</div>
          )}
        </div>
      );
    }
    return <input className={common} type="text" placeholder="Resposta…" disabled />;
  };

  async function handleCreateQuestion(e) {
    e.preventDefault();
    if (!selectedForm) return;
    setCreatingQuestion(true);
    setQuestionsError("");
    try {
      const source =
        selectedQuestion && selectedQuestion.isDraft ? selectedQuestion : newQuestion;
      const payload = {
        label: source.label,
        type: source.type,
        form: selectedForm.id,
        required: !!source.required,
        section: getSuperQuestionId(source) ? Number(getSuperQuestionId(source)) : null,
      };
      const qResp = await authFetch(`${BUILDER_BASE}/questions/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!qResp.ok) {
        const txt = await qResp.text();
        throw new Error(txt || `Erro ${qResp.status} ao criar pergunta`);
      }
      const createdQuestion = await qResp.json();
      if (source.type === "radio" || source.type === "checkbox") {
        const finalList = [
          ...draftOptionItems,
          ...parseOptionEntryText(draftOptionInput),
        ].filter(Boolean);
        const unique = [];
        const seen = new Set();
        for (const x of finalList) {
          const k = x.toLowerCase();
          if (!seen.has(k)) {
            unique.push(x);
            seen.add(k);
          }
        }
        if (unique.length > 0) {
          // O backend cria opções individualmente
          // (não existe submit em lote para opções)
          await Promise.all(
            unique.map((label) =>
              authFetch(`${BUILDER_BASE}/options/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ question: createdQuestion.id, label }),
              })
            )
          );
        }
      }

      setNewQuestion({
        label: "",
        type: "text",
        required: true,
      });
      clearDraftOptions();
      setDraftQuestion(null);
      setDraftAnchorId(null);
      setSelectedQuestion(null);
      await loadQuestions(selectedForm.id);
      showBuilderToast("success", "Pergunta criada.");
    } catch (e) {
      const msg = e?.message || "Erro ao criar pergunta";
      setQuestionsError(msg);
      showBuilderToast("error", msg);
    } finally {
      setCreatingQuestion(false);
    }
  }

  async function handleCreateSection(e) {
    e.preventDefault();
    if (!selectedForm || !newSectionLabel.trim()) return;
    setCreatingSection(true);
    try {
      const resp = await authFetch(`${BUILDER_BASE}/sections/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form: selectedForm.id,
          label: newSectionLabel.trim(),
          section: newSectionParentId ? Number(newSectionParentId) : null,
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao criar secção`);
      }
      setNewSectionLabel("");
      setNewSectionParentId("");
      await loadSuperQuestions(selectedForm.id);
      showBuilderToast("success", "Secção criada.");
    } catch (e) {
      const msg = e?.message || "Erro ao criar secção";
      setQuestionsError(msg);
      showBuilderToast("error", msg);
    } finally {
      setCreatingSection(false);
    }
  }

  async function handleUpdateQuestion(updated) {
    try {
      const payload = {
        label: updated.label,
        type: updated.type,
        required: updated.required,
        form: selectedForm.id,
        section: getSuperQuestionId(updated) ? Number(getSuperQuestionId(updated)) : null,
      };
      const resp = await authFetch(`${BUILDER_BASE}/questions/${updated.id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao actualizar pergunta`);
      }
      await loadQuestions(selectedForm.id);
    } catch (e) {
      const msg = e?.message || "Erro ao actualizar pergunta";
      setQuestionsError(msg);
      showBuilderToast("error", msg);
    }
  }

  async function handleDeleteQuestion(id) {
    if (!window.confirm("Deseja remover esta pergunta? Esta ação não pode ser desfeita.")) return;
    try {
      const resp = await authFetch(`${BUILDER_BASE}/questions/${id}/`, {
        method: "DELETE",
      });
      if (!resp.ok && resp.status !== 204) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao apagar pergunta`);
      }
      if (selectedQuestion && selectedQuestion.id === id) {
        setSelectedQuestion(null);
      }
      await loadQuestions(selectedForm.id);
      showBuilderToast("success", "Pergunta removida.");
    } catch (e) {
      const msg = e?.message || "Erro ao apagar pergunta";
      setQuestionsError(msg);
      showBuilderToast("error", msg);
    }
  }

  function startDraftQuestion({ type = "text", superQuestion = null, anchorId = null } = {}) {
    const draft = {
      id: "__draft__",
      isDraft: true,
      label: "",
      type,
      required: true,
      section: superQuestion,
      options: [],
      form: selectedForm?.id ?? null,
    };
    setDraftAnchorId(anchorId);
    setDraftQuestion(draft);
    setSelectedQuestion(draft);
  }

  function patchSelectedQuestion(updater) {
    setSelectedQuestion((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      if (next && next.isDraft) setDraftQuestion(next);
      return next;
    });
  }

  async function autoSaveIfNeeded(question) {
    if (!question || question.isDraft || !selectedForm) return;
    const original = questions.find((q) => String(q.id) === String(question.id));
    if (!original || !hasQuestionChanged(question, original)) return;
    await handleUpdateQuestion({
      ...question,
      label: question.label || "",
      type: question.type || "text",
      required: !!question.required,
      section: getSuperQuestionId(question) ? Number(getSuperQuestionId(question)) : null,
    });
  }

  async function selectQuestionForEdit(q) {
    if (selectedQuestion && selectedQuestion.id !== q.id) {
      await autoSaveIfNeeded(selectedQuestion);
    }
    setSelectedQuestion(q);
    if (!q?.isDraft) setDraftQuestion(null);
  }

  async function handleAddBelow(question) {
    await autoSaveIfNeeded(selectedQuestion);
    const sectionId = getSuperQuestionId(question);
    const sq = sectionId
      ? superQuestions.find((s) => String(s.id) === String(sectionId)) || null
      : null;
    startDraftQuestion({ type: "text", superQuestion: sq, anchorId: question?.id || null });
  }

  async function handleInlineAddOption(question) {
    if (!question || !newOptionLabel.trim() || !selectedForm) return;
    setAddingOption(true);
    setQuestionsError("");
    try {
      const resp = await authFetch(`${BUILDER_BASE}/options/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: question.id,
          label: newOptionLabel.trim(),
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao adicionar opção`);
      }
      setNewOptionLabel("");
      await loadQuestions(selectedForm.id);
      showBuilderToast("success", "Opção adicionada.");
    } catch (e) {
      const msg = e?.message || "Erro ao adicionar opção";
      setQuestionsError(msg);
      showBuilderToast("error", msg);
    } finally {
      setAddingOption(false);
    }
  }

  async function handleSaveForm(e) {
    e?.preventDefault?.();
    if (!selectedForm || !formDraft) return;
    setSavingForm(true);
    try {
      const payload = {
        title: (formDraft.title || "").trim(),
        description: (formDraft.description || "").trim(),
        code: (formDraft.code || "").trim(),
        target_group: formDraft.target_group,
        active: !!formDraft.active,
      };
      if (!payload.title || !payload.code) {
        throw new Error("Preencha o título e o código do formulário.");
      }
      const resp = await authFetch(`${BUILDER_BASE}/forms/${selectedForm.id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao actualizar formulário`);
      }
      const data = await resp.json();
      setSelectedForm(data);
      setEditingForm(false);
      showBuilderToast("success", "Formulário actualizado.");
      await loadAllForms();
    } catch (err) {
      showBuilderToast("error", err?.message || "Erro ao actualizar formulário");
    } finally {
      setSavingForm(false);
    }
  }

  async function handleUpdateSection(sectionId, newLabel) {
    if (!selectedForm) return;
    setSavingSection(true);
    try {
      const existing = superQuestions.find((s) => String(s.id) === String(sectionId));
      const parentId =
        existing?.section && typeof existing.section === "object"
          ? existing.section.id
          : existing?.section ?? null;
      const resp = await authFetch(`${BUILDER_BASE}/sections/${sectionId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form: selectedForm.id,
          label: (newLabel || "").trim(),
          section: parentId == null || parentId === "" ? null : Number(parentId),
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao actualizar secção`);
      }
      setEditingSectionId(null);
      setEditingSectionLabel("");
      await loadSuperQuestions(selectedForm.id);
      showBuilderToast("success", "Secção actualizada.");
    } catch (err) {
      showBuilderToast("error", err?.message || "Erro ao actualizar secção");
    } finally {
      setSavingSection(false);
    }
  }

  async function handleDeleteSection(section) {
    if (!selectedForm) return;
    if (
      !window.confirm(
        `Remover a secção "${section.label}"? As perguntas desta secção ficam sem secção (ou o servidor pode impedir esta acção).`
      )
    ) {
      return;
    }
    try {
      const resp = await authFetch(`${BUILDER_BASE}/sections/${section.id}/`, {
        method: "DELETE",
      });
      if (!resp.ok && resp.status !== 204) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao apagar secção`);
      }
      await loadSuperQuestions(selectedForm.id);
      await loadQuestions(selectedForm.id);
      showBuilderToast("success", "Secção removida.");
    } catch (err) {
      showBuilderToast("error", err?.message || "Erro ao apagar secção");
    }
  }

  async function handleUpdateOption(option, questionId) {
    if (!option?.id) return;
    setSavingOption(true);
    try {
      const resp = await authFetch(`${BUILDER_BASE}/options/${option.id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: option.question || questionId || selectedQuestion?.id,
          label: (editingOptionLabel || "").trim(),
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao actualizar opção`);
      }
      setEditingOptionId(null);
      setEditingOptionLabel("");
      if (selectedForm?.id) await loadQuestions(selectedForm.id);
      showBuilderToast("success", "Opção actualizada.");
    } catch (err) {
      showBuilderToast("error", err?.message || "Erro ao actualizar opção");
    } finally {
      setSavingOption(false);
    }
  }

  async function handleDeleteOption(option) {
    if (!selectedForm) return;
    if (!option?.id) {
      showBuilderToast("error", "Não foi possível remover: opção inválida.");
      return;
    }
    if (!window.confirm(`Remover a opção "${option.label}"? Esta acção não pode ser desfeita.`)) {
      return;
    }
    try {
      const resp = await authFetch(`${BUILDER_BASE}/options/${option.id}/`, {
        method: "DELETE",
      });
      if (!resp.ok && resp.status !== 204) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao apagar opção`);
      }
      if (selectedForm?.id) await loadQuestions(selectedForm.id);
      showBuilderToast("success", "Opção removida.");
    } catch (err) {
      showBuilderToast("error", err?.message || "Erro ao apagar opção");
    }
  }

  function resetConditionDraft() {
    setConditionDraft({ source_question: "", operator: "equals", option: "", value: "" });
    setEditingConditionId(null);
  }

  async function handleCreateCondition(targetQuestion) {
    if (!selectedForm || !targetQuestion?.id) return;
    setCreatingCondition(true);
    setConditionsError("");
    try {
      const sourceId = String(conditionDraft.source_question || "");
      const source = questions.find((q) => String(q.id) === sourceId);
      if (!sourceId || !source) throw new Error("Escolha a pergunta de referência.");

      const payload = {
        source_question: Number(sourceId),
        operator: conditionDraft.operator,
        option: null,
        value: null,
        target_question: Number(targetQuestion.id),
      };

      if (source.type === "number") {
        const v = String(conditionDraft.value || "").trim();
        if (!v) throw new Error("Introduza o valor numérico para a condição.");
        payload.value = v;
      } else if (source.type === "radio" || source.type === "checkbox") {
        const optId = String(conditionDraft.option || "");
        if (!optId) throw new Error("Escolha a opção para a condição.");
        payload.option = Number(optId);
      } else {
        throw new Error("A pergunta de referência deve ser numérica ou de escolha (única/múltipla).");
      }

      const resp = await authFetch(`${BUILDER_BASE}/question-conditions/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao criar condição`);
      }
      await loadConditions(selectedForm.id);
      resetConditionDraft();
      showBuilderToast("success", "Condição criada.");
    } catch (e) {
      const msg = e?.message || "Erro ao criar condição";
      setConditionsError(msg);
      showBuilderToast("error", msg);
    } finally {
      setCreatingCondition(false);
    }
  }

  async function handleSaveCondition(conditionId, targetQuestion) {
    if (!selectedForm || !conditionId || !targetQuestion?.id) return;
    setSavingCondition(true);
    setConditionsError("");
    try {
      const sourceId = String(conditionDraft.source_question || "");
      const source = questions.find((q) => String(q.id) === sourceId);
      if (!sourceId || !source) throw new Error("Escolha a pergunta de referência.");

      const payload = {
        source_question: Number(sourceId),
        operator: conditionDraft.operator,
        option: null,
        value: null,
        target_question: Number(targetQuestion.id),
      };

      if (source.type === "number") {
        const v = String(conditionDraft.value || "").trim();
        if (!v) throw new Error("Introduza o valor numérico para a condição.");
        payload.value = v;
      } else if (source.type === "radio" || source.type === "checkbox") {
        const optId = String(conditionDraft.option || "");
        if (!optId) throw new Error("Escolha a opção para a condição.");
        payload.option = Number(optId);
      } else {
        throw new Error("A pergunta de referência deve ser numérica ou de escolha (única/múltipla).");
      }

      const resp = await authFetch(`${BUILDER_BASE}/question-conditions/${conditionId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao actualizar condição`);
      }
      await loadConditions(selectedForm.id);
      resetConditionDraft();
      showBuilderToast("success", "Condição actualizada.");
    } catch (e) {
      const msg = e?.message || "Erro ao actualizar condição";
      setConditionsError(msg);
      showBuilderToast("error", msg);
    } finally {
      setSavingCondition(false);
    }
  }

  async function handleDeleteCondition(condition) {
    if (!selectedForm || !condition?.id) return;
    if (!window.confirm("Remover esta condição?")) return;
    try {
      const resp = await authFetch(`${BUILDER_BASE}/question-conditions/${condition.id}/`, {
        method: "DELETE",
      });
      if (!resp.ok && resp.status !== 204) {
        const txt = await resp.text();
        throw new Error(txt || `Erro ${resp.status} ao apagar condição`);
      }
      await loadConditions(selectedForm.id);
      if (editingConditionId === condition.id) resetConditionDraft();
      showBuilderToast("success", "Condição removida.");
    } catch (e) {
      showBuilderToast("error", e?.message || "Erro ao apagar condição");
    }
  }

  return (
    <div className="min-h-screen max-w-7xl mx-auto pt-3 md:pt-4 pb-28 md:pb-24 px-4 sm:px-5 md:px-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-xs sm:text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:border-emerald-400 hover:text-emerald-700 transition-colors"
          >
            ← Voltar
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
            Editar formulário
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">{hintsPt.editPageSubtitle}</p>
        </div>
      </div>

      {loadingForm && (
        <div className="mt-4 text-sm text-gray-500">A carregar formulário…</div>
      )}

      {selectedForm && !loadingForm && (
        <div className="mt-8">
          <div className="bg-emerald-500 text-white rounded-xl px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{selectedForm.title}</div>
              <div className="text-xs text-white/80 truncate">
                {selectedForm.code} • {selectedForm.target_group}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditingForm((v) => {
                    const next = !v;
                    if (!v && selectedForm) {
                      setFormDraft({
                        title: selectedForm.title || "",
                        description: selectedForm.description || "",
                        code: selectedForm.code || "",
                        target_group: selectedForm.target_group || "graduate",
                        active: !!selectedForm.active,
                      });
                    }
                    return next;
                  });
                }}
                className="px-3 py-1.5 rounded-lg text-xs bg-white/10 hover:bg-white/20"
              >
                {editingForm ? "Fechar edição" : "Editar dados"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/formularios")}
                className="px-3 py-1.5 rounded-lg text-xs bg-white/10 hover:bg-white/20"
              >
                Voltar à lista
              </button>
              <button
                type="button"
                onClick={() => setFormSwitcherOpen((o) => !o)}
                className="px-3 py-1.5 rounded-lg text-xs bg-white/10 hover:bg-white/20"
              >
                Editar outro formulário
              </button>
              {formSwitcherOpen && (
                <select
                  className="text-xs text-gray-900 rounded-lg px-2 py-1.5 max-w-[220px] sm:max-w-xs"
                  value={formIdStr || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && v !== formIdStr) {
                      navigate(`/formularios/${v}/edit`, { replace: true });
                      setFormSwitcherOpen(false);
                    }
                  }}
                >
                  {allForms.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.code} — {f.title}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={async () => {
                  await loadAllForms();
                  if (selectedForm?.id) {
                    try {
                      const resp = await authFetch(`${BUILDER_BASE}/forms/${selectedForm.id}/`);
                      if (resp.ok) {
                        const data = await resp.json();
                        setSelectedForm(data);
                      }
                      await Promise.all([
                        loadQuestions(selectedForm.id),
                        loadSuperQuestions(selectedForm.id),
                      ]);
                    } catch (_) {
                      /* ignore */
                    }
                  }
                }}
                className="px-3 py-1.5 rounded-lg text-xs bg-white/10 hover:bg-white/20"
              >
                Actualizar
              </button>
            </div>
          </div>

          {editingForm && formDraft && (
            <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm p-4 sm:p-5">
              <form onSubmit={handleSaveForm} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Título</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formDraft.title}
                    onChange={(e) => setFormDraft((p) => ({ ...p, title: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    rows={2}
                    value={formDraft.description}
                    onChange={(e) => setFormDraft((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Código</label>
                  <FieldHint>{hintsPt.formCodeEdit}</FieldHint>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formDraft.code}
                    onChange={(e) => setFormDraft((p) => ({ ...p, code: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Grupo-alvo</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formDraft.target_group}
                    onChange={(e) => setFormDraft((p) => ({ ...p, target_group: e.target.value }))}
                  >
                    <option value="graduate">Graduado</option>
                    <option value="employer">Empregador</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex items-center justify-between gap-3 pt-1">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!formDraft.active}
                      onChange={(e) => setFormDraft((p) => ({ ...p, active: e.target.checked }))}
                    />
                    Activo
                  </label>
                  <FieldHint className="flex-1 min-w-0">{hintsPt.formActiveEdit}</FieldHint>
                  <button
                    type="submit"
                    disabled={savingForm}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                  >
                    {savingForm ? "A guardar…" : "Guardar"}
                  </button>
                </div>
              </form>
            </div>
          )}

          <p className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-900 leading-relaxed">
            {hintsPt.editFlowCallout}
          </p>

        <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-5 md:gap-7 min-w-0">
          {/* Sidebar: estrutura */}
          <div className="min-w-0 xl:sticky xl:top-24 h-max">
            <Card
              title="Secções"
              right={
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="text-xs text-emerald-700 hover:underline lg:hidden"
                >
                  Pré‑visualizar
                </button>
              }
            >
              {loadingQuestions && <div className="text-sm text-gray-500">A carregar…</div>}
              {questionsError && <div className="text-sm text-red-600 mb-2 break-words">{questionsError}</div>}

              <div className="space-y-4">
                <p className="text-xs text-gray-600 leading-relaxed">{hintsPt.sectionsIntro}</p>

                <form onSubmit={handleCreateSection} className="flex flex-wrap items-stretch gap-2">
                  <input
                    ref={sectionInputRef}
                    className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Nova secção…"
                    value={newSectionLabel}
                    onChange={(e) => setNewSectionLabel(e.target.value)}
                  />
                  <select
                    className="w-full sm:w-auto sm:min-w-[150px] border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
                    value={newSectionParentId}
                    onChange={(e) => setNewSectionParentId(e.target.value)}
                    title="Secção pai (opcional)"
                  >
                    <option value="">(Topo)</option>
                    {superQuestions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    disabled={creatingSection}
                    className="w-full sm:w-auto sm:shrink-0 px-3 py-2 text-xs font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {creatingSection ? "…" : "Adicionar"}
                  </button>
                </form>
                <FieldHint className="mt-2">{hintsPt.sectionParent}</FieldHint>

                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-700 mb-2">Secções</div>
                  {superQuestions.length === 0 ? (
                    <div className="text-sm text-gray-500">Sem secções.</div>
                  ) : (
                    <ul className="space-y-2">
                      {superQuestions.map((s) => {
                        const secQuestions = questions.filter((q) => String(getSuperQuestionId(q)) === String(s.id));
                        const expanded = !!expandedSections[String(s.id)];
                        return (
                          <li key={s.id} className="rounded-lg border border-gray-200 bg-gray-50">
                            <div className="w-full px-3 py-2 flex items-center justify-between gap-2">
                              {editingSectionId === s.id ? (
                                <input
                                  className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  value={editingSectionLabel}
                                  onChange={(e) => setEditingSectionLabel(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedSections((prev) => ({
                                      ...prev,
                                      [String(s.id)]: !prev[String(s.id)],
                                    }))
                                  }
                                  className="flex-1 min-w-0 text-left text-xs font-medium text-gray-800 truncate"
                                  title={s.label}
                                >
                                  {s.label}
                                </button>
                              )}

                              <div className="flex items-center gap-2 shrink-0">
                                {editingSectionId === s.id ? (
                                  <>
                                    <button
                                      type="button"
                                      disabled={savingSection || !editingSectionLabel.trim()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUpdateSection(s.id, editingSectionLabel);
                                      }}
                                      className="text-[11px] text-emerald-700 hover:underline disabled:opacity-60"
                                    >
                                      {savingSection ? "…" : "Guardar"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingSectionId(null);
                                        setEditingSectionLabel("");
                                      }}
                                      className="text-[11px] text-gray-600 hover:underline"
                                    >
                                      Cancelar
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingSectionId(s.id);
                                        setEditingSectionLabel(s.label || "");
                                      }}
                                      className="text-[11px] text-gray-700 hover:underline"
                                    >
                                      Editar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSection(s);
                                      }}
                                      className="text-[11px] text-red-600 hover:underline"
                                    >
                                      Remover
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedSections((prev) => ({
                                          ...prev,
                                          [String(s.id)]: !prev[String(s.id)],
                                        }));
                                      }}
                                      className="text-[11px] text-gray-500 hover:underline"
                                      title={expanded ? "Fechar" : "Abrir"}
                                    >
                                      {expanded ? "−" : "+"}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {expanded && (
                              <div className="px-3 pb-3 space-y-1">
                                {secQuestions.length === 0 ? (
                                  <div className="text-xs text-gray-500">Sem perguntas nesta secção.</div>
                                ) : (
                                  secQuestions.map((q) => (
                                    <div key={q.id} className="rounded border border-gray-200 bg-white px-2 py-1.5">
                                      <div className="text-xs text-gray-800 truncate">{q.label}</div>
                                      <div className="mt-1 flex items-center gap-1">
                                        <button type="button" disabled className="px-2 py-0.5 text-[10px] rounded border border-gray-200 text-gray-400 cursor-not-allowed">↑</button>
                                        <button type="button" disabled className="px-2 py-0.5 text-[10px] rounded border border-gray-200 text-gray-400 cursor-not-allowed">↓</button>
                                        <span className="text-[10px] text-gray-400">Reordenação depende de suporte no endpoint.</span>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Coluna direita: preview + settings */}
          <div className="min-w-0 space-y-4 md:space-y-6">
            <Card
              title="Pré‑visualização"
              right={
                <button
                  type="button"
                  onClick={async () => {
                    await autoSaveIfNeeded(selectedQuestion);
                    setSelectedQuestion(null);
                    setDraftQuestion(null);
                  }}
                  className="text-xs text-gray-600 hover:underline"
                  title="Limpar seleção"
                >
                  Limpar seleção
                </button>
              }
            >
              <FieldHint className="mb-3 px-1">{hintsPt.previewIntro}</FieldHint>
              <div ref={previewBuilderRef} className="grid grid-cols-1 gap-4">
                <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4">
                  <div className="mx-auto max-w-2xl">
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-100" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{selectedForm.title}</div>
                          <div className="text-xs text-gray-500 truncate">{selectedForm.code}</div>
                        </div>
                      </div>
                      <div className="p-4 space-y-4">
                        {previewQuestionsBySection.sections.map((sec) => (
                          <div key={sec.key} className="space-y-3">
                            <div className="text-xs font-semibold text-gray-700">{sec.label}</div>
                            {sec.questions.map((q) => {
                              const current =
                                selectedQuestion && selectedQuestion.id === q.id
                                  ? selectedQuestion
                                  : q;
                              const editing =
                                selectedQuestion && selectedQuestion.id === q.id;
                              return (
                                <div
                                  key={q.id}
                                  onClick={() => selectQuestionForEdit(q)}
                                  className={`rounded-xl border p-3 cursor-pointer ${
                                    editing
                                      ? "border-emerald-300 bg-emerald-50"
                                      : "border-gray-100 bg-white"
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {editing ? (
                                      <input
                                        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        value={current.label || ""}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, label: e.target.value } : prev
                                          )
                                        }
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                        placeholder="Texto da pergunta"
                                      />
                                    ) : (
                                      <div className="text-sm font-medium text-gray-900">
                                        {current.label} {current.required ? <span className="text-red-500">*</span> : null}
                                      </div>
                                    )}
                                    {editing && (
                                      <select
                                        className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                        value={current.type || "text"}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, type: e.target.value } : prev
                                          )
                                        }
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                      >
                                        <option value="text">texto</option>
                                        <option value="textarea">textarea</option>
                                        <option value="number">numérico</option>
                                        <option value="radio">radio</option>
                                        <option value="checkbox">checkbox</option>
                                      </select>
                                    )}
                                    {editing && current.type === "radio" && (
                                      <FieldHint className="mt-1">{hintsPt.questionTypeRadio}</FieldHint>
                                    )}
                                    {editing && current.type === "checkbox" && (
                                      <FieldHint className="mt-1">{hintsPt.questionTypeCheckbox}</FieldHint>
                                    )}
                                  </div>
                                  {editing && (
                                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={!!current.required}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, required: e.target.checked } : prev
                                          )
                                        }
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                      />
                                      Obrigatória
                                    </label>
                                  )}
                                  {editing && (
                                    <div className="mt-2">
                                      <select
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                        value={getSuperQuestionId(current)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const value = e.target.value || null;
                                          const sq =
                                            superQuestions.find((s) => String(s.id) === String(value)) || null;
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, section: sq || null } : prev
                                          );
                                        }}
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                      >
                                        <option value="">(Sem secção)</option>
                                        {superQuestions.map((s) => (
                                          <option key={s.id} value={s.id}>
                                            {s.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                  <div className="mt-2">{renderPreviewField(current)}</div>
                                  {Array.isArray(current.options) && current.options.length > 0 && (
                                    <div className="mt-2 text-[11px] text-gray-500 truncate">
                                      {current.options.map((o) => o.label).join(" • ")}
                                    </div>
                                  )}
                                  {editing &&
                                    !current.isDraft &&
                                    (current.type === "radio" || current.type === "checkbox") &&
                                    Array.isArray(current.options) && (
                                      <div className="mt-2 space-y-1">
                                        {current.options.length === 0 ? (
                                          <div className="text-[11px] text-gray-500">Sem opções.</div>
                                        ) : (
                                          current.options.map((o) => (
                                            <div
                                              key={o.id || o.label}
                                              className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-white px-2 py-1"
                                            >
                                              {editingOptionId === o.id ? (
                                                <input
                                                  className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                  value={editingOptionLabel}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => setEditingOptionLabel(e.target.value)}
                                                />
                                              ) : (
                                                <div className="flex-1 min-w-0 text-xs text-gray-800 truncate" title={o.label}>
                                                  {o.label}
                                                </div>
                                              )}
                                              <div className="shrink-0 flex items-center gap-2">
                                                {editingOptionId === o.id ? (
                                                  <>
                                                    <button
                                                      type="button"
                                                      disabled={savingOption || !editingOptionLabel.trim()}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdateOption(o, current.id);
                                                      }}
                                                      className="text-[11px] text-emerald-700 hover:underline disabled:opacity-60"
                                                    >
                                                      {savingOption ? "…" : "Guardar"}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingOptionId(null);
                                                        setEditingOptionLabel("");
                                                      }}
                                                      className="text-[11px] text-gray-600 hover:underline"
                                                    >
                                                      Cancelar
                                                    </button>
                                                  </>
                                                ) : (
                                                  <>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!o.id) return;
                                                        setEditingOptionId(o.id);
                                                        setEditingOptionLabel(o.label || "");
                                                      }}
                                                      className="text-[11px] text-gray-700 hover:underline"
                                                      disabled={!o.id}
                                                      title={!o.id ? "Sem id da opção" : "Editar opção"}
                                                    >
                                                      Editar
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteOption(o);
                                                      }}
                                                      className="text-[11px] text-red-600 hover:underline"
                                                      disabled={!o.id}
                                                      title={!o.id ? "Sem id da opção" : "Remover opção"}
                                                    >
                                                      Remover
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    )}
                                  {editing && current.isDraft && (
                                    <div className="mt-2">
                                      {(current.type === "radio" || current.type === "checkbox") && (
                                        <DraftOptionsInput
                                          inputValue={draftOptionInput}
                                          onInputChange={setDraftOptionInput}
                                          items={draftOptionItems}
                                          onAddLabels={addDraftOptionLabels}
                                          onRemoveItem={(lab) =>
                                            setDraftOptionItems((prev) => prev.filter((x) => x !== lab))
                                          }
                                          onRemoveLastItem={() =>
                                            setDraftOptionItems((prev) => prev.slice(0, -1))
                                          }
                                          justAdded={draftOptionJustAdded}
                                          stopPropagation
                                        />
                                      )}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCreateQuestion(e);
                                        }}
                                        disabled={creatingQuestion}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                      >
                                        {creatingQuestion ? "A criar…" : "Criar pergunta"}
                                      </button>
                                    </div>
                                  )}
                                  {editing &&
                                    !current.isDraft &&
                                    (current.type === "radio" || current.type === "checkbox") && (
                                      <div className="mt-2">
                                        <div className="flex gap-2">
                                        <input
                                          className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                          placeholder="Nova opção..."
                                          value={newOptionLabel}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => setNewOptionLabel(e.target.value)}
                                        />
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleInlineAddOption(current);
                                          }}
                                          disabled={!newOptionLabel.trim() || addingOption}
                                          className="px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                        >
                                          {addingOption ? "…" : "+ Opção"}
                                        </button>
                                        </div>
                                        <FieldHint>{hintsPt.optionsSavedHelp}</FieldHint>
                                      </div>
                                    )}
                                  {editing && !current.isDraft && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="text-xs font-semibold text-gray-800">
                                          Mostrar quando…
                                        </div>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            resetConditionDraft();
                                          }}
                                          className="text-[11px] text-gray-600 hover:underline"
                                          title="Limpar edição"
                                        >
                                          Limpar
                                        </button>
                                      </div>

                                      {conditionsError && (
                                        <div className="mt-2 text-[11px] text-red-600 break-words">
                                          {conditionsError}
                                        </div>
                                      )}
                                      {loadingConditions ? (
                                        <div className="mt-2 text-[11px] text-gray-500">A carregar condições…</div>
                                      ) : (
                                        <div className="mt-2 space-y-2">
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div>
                                              <label className="block text-[11px] text-gray-600 mb-1">Pergunta de referência</label>
                                              <select
                                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                                value={conditionDraft.source_question}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  setConditionDraft((p) => ({
                                                    ...p,
                                                    source_question: v,
                                                    option: "",
                                                    value: "",
                                                  }));
                                                }}
                                              >
                                                <option value="">(Escolher)</option>
                                                {questions
                                                  .filter(
                                                    (q) =>
                                                      String(q.id) !== String(current.id) &&
                                                      (q.type === "number" ||
                                                        q.type === "radio" ||
                                                        q.type === "checkbox")
                                                  )
                                                  .map((q) => (
                                                    <option key={q.id} value={q.id}>
                                                      {q.label || `(Pergunta ${q.id})`}
                                                    </option>
                                                  ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-[11px] text-gray-600 mb-1">Regra</label>
                                              <select
                                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                                value={conditionDraft.operator}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                  setConditionDraft((p) => ({ ...p, operator: e.target.value }))
                                                }
                                              >
                                                <option value="equals">é igual a</option>
                                                <option value="not_equals">é diferente de</option>
                                                <option value="greater_than">é maior que</option>
                                                <option value="less_than">é menor que</option>
                                              </select>
                                            </div>
                                          </div>

                                          {(() => {
                                            const src = questions.find(
                                              (q) => String(q.id) === String(conditionDraft.source_question)
                                            );
                                            if (!src) return null;
                                            if (src.type === "number") {
                                              return (
                                                <div>
                                                  <label className="block text-[11px] text-gray-600 mb-1">Valor</label>
                                                  <input
                                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    value={conditionDraft.value}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                      setConditionDraft((p) => ({ ...p, value: e.target.value }))
                                                    }
                                                    placeholder="Ex.: 10"
                                                  />
                                                </div>
                                              );
                                            }
                                            if (src.type === "radio" || src.type === "checkbox") {
                                              const opts = Array.isArray(src.options) ? src.options : [];
                                              return (
                                                <div>
                                                  <label className="block text-[11px] text-gray-600 mb-1">Opção</label>
                                                  <select
                                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                                    value={conditionDraft.option}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                      setConditionDraft((p) => ({ ...p, option: e.target.value }))
                                                    }
                                                  >
                                                    <option value="">(Escolher)</option>
                                                    {opts.map((o) => (
                                                      <option key={o.id || o.label} value={o.id || ""} disabled={!o.id}>
                                                        {o.label}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                              );
                                            }
                                            return null;
                                          })()}

                                          <div className="flex items-center justify-between gap-2">
                                            <FieldHint>{hintsPt.conditionsIntro}</FieldHint>
                                            <FieldHint>{hintsPt.conditionsNumber}</FieldHint>
                                            {editingConditionId ? (
                                              <button
                                                type="button"
                                                disabled={savingCondition}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSaveCondition(editingConditionId, current);
                                                }}
                                                className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                              >
                                                {savingCondition ? "A guardar…" : "Guardar"}
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                disabled={creatingCondition}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCreateCondition(current);
                                                }}
                                                className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                              >
                                                {creatingCondition ? "A criar…" : "Adicionar"}
                                              </button>
                                            )}
                                          </div>

                                          <div className="pt-2 border-t border-gray-100 space-y-1">
                                            {conditions.filter((c) => String(c.target_question) === String(current.id))
                                              .length === 0 ? (
                                              <div className="text-[11px] text-gray-500">{hintsPt.conditionsEmpty}</div>
                                            ) : (
                                              conditions
                                                .filter((c) => String(c.target_question) === String(current.id))
                                                .map((c) => {
                                                  const src = questions.find(
                                                    (q) => String(q.id) === String(c.source_question)
                                                  );
                                                  const optLabel =
                                                    src && (src.type === "radio" || src.type === "checkbox")
                                                      ? (src.options || []).find(
                                                          (o) => String(o.id) === String(c.option)
                                                        )?.label
                                                      : null;
                                                  const operatorLabel =
                                                    c.operator === "equals"
                                                      ? "é igual a"
                                                      : c.operator === "not_equals"
                                                        ? "é diferente de"
                                                        : c.operator === "greater_than"
                                                          ? "é maior que"
                                                          : c.operator === "less_than"
                                                            ? "é menor que"
                                                            : c.operator;
                                                  return (
                                                    <div
                                                      key={c.id}
                                                      className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1"
                                                    >
                                                      <div className="min-w-0 text-[11px] text-gray-700 truncate">
                                                        <span className="font-medium">Mostrar</span>{" "}
                                                        quando{" "}
                                                        <span className="font-medium">
                                                          {src?.label || `(Pergunta ${c.source_question})`}
                                                        </span>{" "}
                                                        {operatorLabel}{" "}
                                                        <span className="font-medium">
                                                          {optLabel || c.value || "(…)"}
                                                        </span>
                                                      </div>
                                                      <div className="shrink-0 flex items-center gap-2">
                                                        <button
                                                          type="button"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingConditionId(c.id);
                                                            setConditionDraft({
                                                              source_question: String(c.source_question || ""),
                                                              operator: c.operator || "equals",
                                                              option: c.option != null ? String(c.option) : "",
                                                              value: c.value != null ? String(c.value) : "",
                                                            });
                                                          }}
                                                          className="text-[11px] text-gray-700 hover:underline"
                                                        >
                                                          Editar
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteCondition(c);
                                                          }}
                                                          className="text-[11px] text-red-600 hover:underline"
                                                        >
                                                          Remover
                                                        </button>
                                                      </div>
                                                    </div>
                                                  );
                                                })
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {!current.isDraft && (
                                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddBelow(current);
                                        }}
                                        className="text-xs text-emerald-700 hover:underline"
                                      >
                                        Adicionar pergunta
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteQuestion(current.id);
                                        }}
                                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                                        title="Apagar pergunta"
                                      >
                                        <FaTrashAlt aria-hidden />
                                        <span className="sr-only">Apagar</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                        {previewQuestionsBySection.noSection.questions.length > 0 && (
                          <div className="space-y-3">
                            <div className="text-xs font-semibold text-gray-700">Sem secção</div>
                            {previewQuestionsBySection.noSection.questions.map((q) => {
                              const current =
                                selectedQuestion && selectedQuestion.id === q.id
                                  ? selectedQuestion
                                  : q;
                              const editing =
                                selectedQuestion && selectedQuestion.id === q.id;
                              return (
                                <div
                                  key={q.id}
                                  onClick={() => selectQuestionForEdit(q)}
                                  className={`rounded-xl border p-3 cursor-pointer ${
                                    editing
                                      ? "border-emerald-300 bg-emerald-50"
                                      : "border-gray-100 bg-white"
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {editing ? (
                                      <input
                                        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        value={current.label || ""}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, label: e.target.value } : prev
                                          )
                                        }
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                        placeholder="Texto da pergunta"
                                      />
                                    ) : (
                                      <div className="text-sm font-medium text-gray-900">
                                        {current.label} {current.required ? <span className="text-red-500">*</span> : null}
                                      </div>
                                    )}
                                    {editing && (
                                      <select
                                        className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                        value={current.type || "text"}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, type: e.target.value } : prev
                                          )
                                        }
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                      >
                                        <option value="text">texto</option>
                                        <option value="textarea">textarea</option>
                                        <option value="number">numérico</option>
                                        <option value="radio">radio</option>
                                        <option value="checkbox">checkbox</option>
                                      </select>
                                    )}
                                    {editing && current.type === "radio" && (
                                      <FieldHint className="mt-1">{hintsPt.questionTypeRadio}</FieldHint>
                                    )}
                                    {editing && current.type === "checkbox" && (
                                      <FieldHint className="mt-1">{hintsPt.questionTypeCheckbox}</FieldHint>
                                    )}
                                  </div>
                                  {editing && (
                                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={!!current.required}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) =>
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, required: e.target.checked } : prev
                                          )
                                        }
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                      />
                                      Obrigatória
                                    </label>
                                  )}
                                  {editing && (
                                    <div className="mt-2">
                                      <select
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                        value={getSuperQuestionId(current)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const value = e.target.value || null;
                                          const sq =
                                            superQuestions.find((s) => String(s.id) === String(value)) || null;
                                          patchSelectedQuestion((prev) =>
                                            prev ? { ...prev, section: sq || null } : prev
                                          );
                                        }}
                                        onBlur={() => autoSaveIfNeeded(selectedQuestion)}
                                      >
                                        <option value="">(Sem secção)</option>
                                        {superQuestions.map((s) => (
                                          <option key={s.id} value={s.id}>
                                            {s.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                  <div className="mt-2">{renderPreviewField(current)}</div>
                                  {Array.isArray(current.options) && current.options.length > 0 && (
                                    <div className="mt-2 text-[11px] text-gray-500 truncate">
                                      {current.options.map((o) => o.label).join(" • ")}
                                    </div>
                                  )}
                                  {editing &&
                                    !current.isDraft &&
                                    (current.type === "radio" || current.type === "checkbox") &&
                                    Array.isArray(current.options) && (
                                      <div className="mt-2 space-y-1">
                                        {current.options.length === 0 ? (
                                          <div className="text-[11px] text-gray-500">Sem opções.</div>
                                        ) : (
                                          current.options.map((o) => (
                                            <div
                                              key={o.id || o.label}
                                              className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-white px-2 py-1"
                                            >
                                              {editingOptionId === o.id ? (
                                                <input
                                                  className="flex-1 min-w-0 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                  value={editingOptionLabel}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => setEditingOptionLabel(e.target.value)}
                                                />
                                              ) : (
                                                <div className="flex-1 min-w-0 text-xs text-gray-800 truncate" title={o.label}>
                                                  {o.label}
                                                </div>
                                              )}
                                              <div className="shrink-0 flex items-center gap-2">
                                                {editingOptionId === o.id ? (
                                                  <>
                                                    <button
                                                      type="button"
                                                      disabled={savingOption || !editingOptionLabel.trim()}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUpdateOption(o, current.id);
                                                      }}
                                                      className="text-[11px] text-emerald-700 hover:underline disabled:opacity-60"
                                                    >
                                                      {savingOption ? "…" : "Guardar"}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingOptionId(null);
                                                        setEditingOptionLabel("");
                                                      }}
                                                      className="text-[11px] text-gray-600 hover:underline"
                                                    >
                                                      Cancelar
                                                    </button>
                                                  </>
                                                ) : (
                                                  <>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!o.id) return;
                                                        setEditingOptionId(o.id);
                                                        setEditingOptionLabel(o.label || "");
                                                      }}
                                                      className="text-[11px] text-gray-700 hover:underline"
                                                      disabled={!o.id}
                                                      title={!o.id ? "Sem id da opção" : "Editar opção"}
                                                    >
                                                      Editar
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteOption(o);
                                                      }}
                                                      className="text-[11px] text-red-600 hover:underline"
                                                      disabled={!o.id}
                                                      title={!o.id ? "Sem id da opção" : "Remover opção"}
                                                    >
                                                      Remover
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    )}
                                  {editing && current.isDraft && (
                                    <div className="mt-2">
                                      {(current.type === "radio" || current.type === "checkbox") && (
                                        <DraftOptionsInput
                                          inputValue={draftOptionInput}
                                          onInputChange={setDraftOptionInput}
                                          items={draftOptionItems}
                                          onAddLabels={addDraftOptionLabels}
                                          onRemoveItem={(lab) =>
                                            setDraftOptionItems((prev) => prev.filter((x) => x !== lab))
                                          }
                                          onRemoveLastItem={() =>
                                            setDraftOptionItems((prev) => prev.slice(0, -1))
                                          }
                                          justAdded={draftOptionJustAdded}
                                          stopPropagation
                                        />
                                      )}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCreateQuestion(e);
                                        }}
                                        disabled={creatingQuestion}
                                        className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                      >
                                        {creatingQuestion ? "A criar…" : "Criar pergunta"}
                                      </button>
                                    </div>
                                  )}
                                  {editing &&
                                    !current.isDraft &&
                                    (current.type === "radio" || current.type === "checkbox") && (
                                      <div className="mt-2">
                                        <div className="flex gap-2">
                                        <input
                                          className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                          placeholder="Nova opção..."
                                          value={newOptionLabel}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => setNewOptionLabel(e.target.value)}
                                        />
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleInlineAddOption(current);
                                          }}
                                          disabled={!newOptionLabel.trim() || addingOption}
                                          className="px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                        >
                                          {addingOption ? "…" : "+ Opção"}
                                        </button>
                                        </div>
                                        <FieldHint>{hintsPt.optionsSavedHelp}</FieldHint>
                                      </div>
                                    )}
                                  {editing && !current.isDraft && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="text-xs font-semibold text-gray-800">
                                          Mostrar quando…
                                        </div>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            resetConditionDraft();
                                          }}
                                          className="text-[11px] text-gray-600 hover:underline"
                                          title="Limpar edição"
                                        >
                                          Limpar
                                        </button>
                                      </div>

                                      {conditionsError && (
                                        <div className="mt-2 text-[11px] text-red-600 break-words">
                                          {conditionsError}
                                        </div>
                                      )}
                                      {loadingConditions ? (
                                        <div className="mt-2 text-[11px] text-gray-500">A carregar condições…</div>
                                      ) : (
                                        <div className="mt-2 space-y-2">
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div>
                                              <label className="block text-[11px] text-gray-600 mb-1">Pergunta de referência</label>
                                              <select
                                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                                value={conditionDraft.source_question}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                  const v = e.target.value;
                                                  setConditionDraft((p) => ({
                                                    ...p,
                                                    source_question: v,
                                                    option: "",
                                                    value: "",
                                                  }));
                                                }}
                                              >
                                                <option value="">(Escolher)</option>
                                                {questions
                                                  .filter(
                                                    (q) =>
                                                      String(q.id) !== String(current.id) &&
                                                      (q.type === "number" ||
                                                        q.type === "radio" ||
                                                        q.type === "checkbox")
                                                  )
                                                  .map((q) => (
                                                    <option key={q.id} value={q.id}>
                                                      {q.label || `(Pergunta ${q.id})`}
                                                    </option>
                                                  ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-[11px] text-gray-600 mb-1">Regra</label>
                                              <select
                                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                                value={conditionDraft.operator}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                  setConditionDraft((p) => ({ ...p, operator: e.target.value }))
                                                }
                                              >
                                                <option value="equals">é igual a</option>
                                                <option value="not_equals">é diferente de</option>
                                                <option value="greater_than">é maior que</option>
                                                <option value="less_than">é menor que</option>
                                              </select>
                                            </div>
                                          </div>

                                          {(() => {
                                            const src = questions.find(
                                              (q) => String(q.id) === String(conditionDraft.source_question)
                                            );
                                            if (!src) return null;
                                            if (src.type === "number") {
                                              return (
                                                <div>
                                                  <label className="block text-[11px] text-gray-600 mb-1">Valor</label>
                                                  <input
                                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                    value={conditionDraft.value}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                      setConditionDraft((p) => ({ ...p, value: e.target.value }))
                                                    }
                                                    placeholder="Ex.: 10"
                                                  />
                                                </div>
                                              );
                                            }
                                            if (src.type === "radio" || src.type === "checkbox") {
                                              const opts = Array.isArray(src.options) ? src.options : [];
                                              return (
                                                <div>
                                                  <label className="block text-[11px] text-gray-600 mb-1">Opção</label>
                                                  <select
                                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                                                    value={conditionDraft.option}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) =>
                                                      setConditionDraft((p) => ({ ...p, option: e.target.value }))
                                                    }
                                                  >
                                                    <option value="">(Escolher)</option>
                                                    {opts.map((o) => (
                                                      <option key={o.id || o.label} value={o.id || ""} disabled={!o.id}>
                                                        {o.label}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                              );
                                            }
                                            return null;
                                          })()}

                                          <div className="flex items-center justify-between gap-2">
                                            <div>
                                              <FieldHint>{hintsPt.conditionsIntro}</FieldHint>
                                              <FieldHint>{hintsPt.conditionsNumber}</FieldHint>
                                            </div>
                                            {editingConditionId ? (
                                              <button
                                                type="button"
                                                disabled={savingCondition}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSaveCondition(editingConditionId, current);
                                                }}
                                                className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                              >
                                                {savingCondition ? "A guardar…" : "Guardar"}
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                disabled={creatingCondition}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCreateCondition(current);
                                                }}
                                                className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                                              >
                                                {creatingCondition ? "A criar…" : "Adicionar"}
                                              </button>
                                            )}
                                          </div>

                                          <div className="pt-2 border-t border-gray-100 space-y-1">
                                            {conditions.filter((c) => String(c.target_question) === String(current.id))
                                              .length === 0 ? (
                                              <div className="text-[11px] text-gray-500">{hintsPt.conditionsEmpty}</div>
                                            ) : (
                                              conditions
                                                .filter((c) => String(c.target_question) === String(current.id))
                                                .map((c) => {
                                                  const src = questions.find(
                                                    (q) => String(q.id) === String(c.source_question)
                                                  );
                                                  const optLabel =
                                                    src && (src.type === "radio" || src.type === "checkbox")
                                                      ? (src.options || []).find(
                                                          (o) => String(o.id) === String(c.option)
                                                        )?.label
                                                      : null;
                                                  const operatorLabel =
                                                    c.operator === "equals"
                                                      ? "é igual a"
                                                      : c.operator === "not_equals"
                                                        ? "é diferente de"
                                                        : c.operator === "greater_than"
                                                          ? "é maior que"
                                                          : c.operator === "less_than"
                                                            ? "é menor que"
                                                            : c.operator;
                                                  return (
                                                    <div
                                                      key={c.id}
                                                      className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1"
                                                    >
                                                      <div className="min-w-0 text-[11px] text-gray-700 truncate">
                                                        <span className="font-medium">Mostrar</span>{" "}
                                                        quando{" "}
                                                        <span className="font-medium">
                                                          {src?.label || `(Pergunta ${c.source_question})`}
                                                        </span>{" "}
                                                        {operatorLabel}{" "}
                                                        <span className="font-medium">
                                                          {optLabel || c.value || "(…)"}
                                                        </span>
                                                      </div>
                                                      <div className="shrink-0 flex items-center gap-2">
                                                        <button
                                                          type="button"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingConditionId(c.id);
                                                            setConditionDraft({
                                                              source_question: String(c.source_question || ""),
                                                              operator: c.operator || "equals",
                                                              option: c.option != null ? String(c.option) : "",
                                                              value: c.value != null ? String(c.value) : "",
                                                            });
                                                          }}
                                                          className="text-[11px] text-gray-700 hover:underline"
                                                        >
                                                          Editar
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteCondition(c);
                                                          }}
                                                          className="text-[11px] text-red-600 hover:underline"
                                                        >
                                                          Remover
                                                        </button>
                                                      </div>
                                                    </div>
                                                  );
                                                })
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {!current.isDraft && (
                                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddBelow(current);
                                        }}
                                        className="text-xs text-emerald-700 hover:underline"
                                      >
                                        Adicionar pergunta
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteQuestion(current.id);
                                        }}
                                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                                        title="Apagar pergunta"
                                      >
                                        <FaTrashAlt aria-hidden />
                                        <span className="sr-only">Apagar</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {previewQuestionsBySection.sections.every((sec) => sec.questions.length === 0) &&
                          previewQuestionsBySection.noSection.questions.length === 0 && (
                            <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center">
                              <div className="text-sm text-gray-500 mb-2">{hintsPt.previewEmptyQuestions}</div>
                              <button
                                type="button"
                                onClick={() => startDraftQuestion({ type: "text" })}
                                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600"
                              >
                                + Criar primeira pergunta
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
        </div>
      )}

      {showPreview && selectedForm && (
        <div className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center p-4 lg:hidden">
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">Pré‑visualização</div>
              <button type="button" onClick={() => setShowPreview(false)} className="text-xs text-gray-600 hover:underline">Fechar</button>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto space-y-3">
              {questions.map((q) => (
                <div key={q.id} className="rounded-xl border border-gray-100 p-3 bg-white">
                  <div className="text-sm font-medium text-gray-900">
                    {q.label} {q.required ? <span className="text-red-500">*</span> : null}
                  </div>
                  <div className="mt-2">{renderPreviewField(q)}</div>
                </div>
              ))}
              {questions.length === 0 && <div className="text-sm text-gray-500">Ainda não existem perguntas.</div>}
            </div>
          </div>
        </div>
      )}

      {builderToast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[1300] max-w-md w-[calc(100%-2rem)] px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
            builderToast.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-red-50 border-red-200 text-red-900"
          }`}
        >
          {builderToast.message}
        </div>
      )}
    </div>
  );
}

